import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { sendPasswordReset } from '../../services/authService';
import { FirebaseError } from 'firebase/app';
import './Auth.css';

interface ForgotPasswordProps {
  onClose: () => void;
  onBackToSignIn: () => void;
}

export default function ForgotPassword({ onClose, onBackToSignIn }: ForgotPasswordProps) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('auth.errors.invalidEmail') || 'Invalid email address')
      .required(t('auth.errors.emailRequired') || 'Email is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setError('');
      setSuccess(false);
      setLoading(true);

      const trimmedEmail = values.email.trim();

      try {
        await sendPasswordReset(trimmedEmail);
        console.log('Password reset email sent successfully');
        setSuccess(true);
      } catch (err) {
        console.error('Password reset failed:', err);
        if (err instanceof FirebaseError) {
          console.error('Firebase error code:', err.code);
          console.error('Firebase error message:', err.message);
          switch (err.code) {
          case 'auth/invalid-email':
            setError(t('auth.errors.invalidEmail'));
            break;
          case 'auth/user-not-found':
            // Deliberately do not reveal whether the user exists.
            // Always show a generic success message so the UI response is identical
            // for existing and non-existing emails. This prevents attackers from
            // using password reset responses to enumerate valid accounts
            // (see security design docs / user enumeration protections).
            setSuccess(true);
            if (process.env.NODE_ENV === 'development') {
              console.warn('User not found; returning generic success response to prevent user enumeration');
            }
            break;
          default:
            setError(t('auth.errors.passwordResetFailed'));
          }
        } else {
          setError(t('auth.errors.passwordResetFailed'));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="auth-form">
      {error && <div className="auth-error">{error}</div>}
      {success && (
        <div className="auth-success">
          {t('auth.passwordResetSent')}
        </div>
      )}

      {!success ? (
        <>
          <p className="auth-description">{t('auth.passwordResetDescription')}</p>
          <form onSubmit={formik.handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
                placeholder={t('auth.emailPlaceholder')}
              />
              {formik.touched.email && formik.errors.email && (
                <span className="error-message">{formik.errors.email}</span>
              )}
            </div>

            <button type="submit" className="auth-submit-button" disabled={loading}>
              {loading ? t('auth.sending') : t('auth.sendResetLink')}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              <button type="button" className="auth-link" onClick={onBackToSignIn}>
                {t('auth.backToSignIn')}
              </button>
            </p>
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            className="auth-submit-button"
            onClick={onBackToSignIn}
          >
            {t('auth.backToSignIn')}
          </button>
          <div className="auth-footer">
            <p>
              <button type="button" className="auth-link" onClick={onClose}>
                {t('auth.close')}
              </button>
            </p>
          </div>
        </>
      )}
    </div>
  );
}
